/**
 * Plan, payment, and what has been paid.
 *
 * Built around one thing the customer must never be confused about: **choosing
 * a plan is not the same as having it.** Checkout opens an invoice and sends
 * them to Paystack; the plan moves when the money does.
 *
 * So the page never optimistically shows the new plan, the button says what it
 * will charge rather than "Upgrade", and a pending change is shown as a
 * pending invoice rather than as a silent nothing. The place this is most
 * likely to be misread is a customer who abandoned checkout and came back — for
 * them the account must plainly say "unchanged, and here is the unpaid bill".
 */

import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Clock, CreditCard } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { ApiError, api } from '../../lib/api';
import { subscriptionStatusBadge } from '../../lib/subscription';
import { planCardView, type PlanFacts } from '../../lib/planCard';
import { Badge, Button, Card, CardHeader, Modal, Spinner } from '../../components/ui';
import { AiUsageCard } from './AiUsageCard';
import { CapacityCard } from './CapacityCard';

interface PlanPrice {
  currencyCode: string;
  interval: string;
  amount: string;
}

interface Plan {
  key: string;
  name: string;
  description: string | null;
  prices: PlanPrice[];
  /**
   * Which of those prices actually applies to this business, decided by the
   * server. A plan is sold in several currencies and the browser must not be
   * the thing that picks — see the note on the price lookup below.
   */
  billingCurrency: string | null;
  trialDays: number;
  modules: string[];
  isCurrent: boolean;
  limits?: PlanFacts['limits'];
  state?: PlanFacts['state'];
  includedForBusinessType?: string[];
  impact?: PlanFacts['impact'];
}

interface Subscription {
  planKey: string;
  planName: string;
  status: string;
  inTrial: boolean;
  trialDaysLeft: number | null;
  trialExpired: boolean;
  pendingPlanKey: string | null;
  pendingInterval?: string | null;
  interval: string;
  currencyCode: string;
  graceUntil: string | null;
  cancelAtPeriodEnd: boolean;
  periodEnd: string;
  complimentaryUntil?: string | null;
  standing?: string;
}

interface Invoice {
  id: string;
  number: string;
  planName: string;
  interval: string;
  amount: string;
  currencyCode: string;
  status: string;
  paidAt: string | null;
  createdAt: string;
}

const money = (amount: string, currency: string): string =>
  `${currency} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export function BillingPage(): ReactNode {
  const { business, can } = useAuth();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const path = (suffix: string) => `/businesses/${business?.businessId}${suffix}`;

  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');

  /**
   * Null means "whatever the server decides for us", which is the right
   * default: it knows the business's country, what it has paid in before and
   * which currencies CenoX actually sells in. A value here is an explicit
   * choice by someone who knows better — a Nigerian company with a dollar
   * account, typically — and it is sent to the server as a request, not an
   * instruction. The server still refuses currencies the plan is not sold in.
   */
  const [currency, setCurrency] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  /** A plan that takes something away, waiting for the customer to confirm. */
  const [confirming, setConfirming] = useState<{ plan: Plan; warnings: string[] } | null>(null);
  /** Shown after choosing a plan while billing is manual: how to actually pay. */
  const [howToPay, setHowToPay] = useState<string | null>(null);

  const subscription = useQuery({
    queryKey: ['subscription', business?.businessId],
    queryFn: () => api.get<Subscription>(path('/subscription')),
    enabled: Boolean(business),
  });

  const plans = useQuery({
    queryKey: ['plans', business?.businessId, currency],
    queryFn: () =>
      api.get<{ data: Plan[]; paymentMode?: 'online' | 'manual'; manualInstructions?: string | null }>(
        path(currency ? `/plans?currency=${encodeURIComponent(currency)}` : '/plans'),
      ),
    enabled: Boolean(business),
  });

  const invoices = useQuery({
    queryKey: ['invoices', business?.businessId],
    queryFn: () => api.get<{ data: Invoice[] }>(path('/invoices')),
    enabled: Boolean(business),
  });

  /**
   * Coming back from Paystack.
   *
   * The reference in the URL is a claim. This asks the server, which asks
   * Paystack server-to-server — the page concludes nothing from having been
   * redirected, because anybody can type a redirect.
   */
  const reference = params.get('reference');
  const confirmation = useQuery({
    queryKey: ['confirm', reference],
    queryFn: () =>
      api.get<{ status: string; message: string }>(
        path(`/subscription/confirm?reference=${encodeURIComponent(reference ?? '')}`),
      ),
    enabled: Boolean(business && reference),
    retry: false,
  });

  /**
   * Refresh once the confirmation lands.
   *
   * Without this the banner says "your plan is active" while the card beneath
   * it still shows the trial — the cached subscription was fetched before the
   * payment cleared. Two statements contradicting each other on one screen is
   * worse than either of them alone, and it is the moment a customer is most
   * likely to think the payment failed and try again.
   */
  const confirmedPlan = confirmation.data?.status;
  const [settledFor, setSettledFor] = useState<string | null>(null);
  if (confirmedPlan === 'activated' && settledFor !== reference) {
    setSettledFor(reference);
    for (const key of ['subscription', 'plans', 'invoices', 'usage', 'modules']) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  }

  const refreshAll = (): void => {
    for (const key of ['subscription', 'plans', 'invoices', 'usage', 'modules']) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  };

  const checkout = useMutation({
    mutationFn: ({ planKey, acknowledgeChanges }: { planKey: string; acknowledgeChanges?: boolean }) =>
      api.post<{ paymentUrl: string | null; reference: string; invoice: Invoice; instructions?: string | null }>(
        path('/subscription/checkout'),
        // Note what is sent and what is not: a currency PREFERENCE, never an
        // amount. The price is looked up server-side from the plan catalogue
        // for whichever currency the server settles on.
        {
          planKey,
          interval,
          ...(currency ? { currencyCode: currency } : {}),
          ...(acknowledgeChanges ? { acknowledgeChanges: true } : {}),
        },
      ),
    onSuccess: (result) => {
      setError(null);
      setConfirming(null);
      refreshAll();
      if (result.paymentUrl) window.location.href = result.paymentUrl;
      // No provider to send them to: say exactly what to do, instead of a
      // button that silently opened another invoice every time it was pressed.
      else if (result.instructions)
        setHowToPay(`Invoice ${result.invoice.number} — ${money(result.invoice.amount, result.invoice.currencyCode)}. ${result.instructions}`);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not start checkout.'),
  });

  // An invoice already open — a renewal, usually — paid for its own amount.
  const payInvoice = useMutation({
    mutationFn: (invoiceId: string) =>
      api.post<{ paymentUrl: string | null; invoice: Invoice; instructions?: string | null }>(
        path(`/invoices/${invoiceId}/pay`),
        {},
      ),
    onSuccess: (result) => {
      setError(null);
      if (result.paymentUrl) window.location.href = result.paymentUrl;
      else if (result.instructions)
        setHowToPay(`Invoice ${result.invoice.number} — ${money(result.invoice.amount, result.invoice.currencyCode)}. ${result.instructions}`);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not start the payment.'),
  });

  const cancel = useMutation({
    mutationFn: (reason: string) => api.post(path('/subscription/cancel'), { reason }),
    onSuccess: () => {
      setCancelling(false);
      refreshAll();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not cancel.'),
  });

  const resume = useMutation({
    mutationFn: () => api.post(path('/subscription/resume')),
    onSuccess: refreshAll,
  });

  /**
   * Which currencies this business could pay in.
   *
   * The union of every published price, not a hard-coded list — a new currency
   * becomes offerable by inserting price rows, with no deploy. The switcher
   * only appears when there is genuinely more than one, because offering a
   * single-item choice is just clutter.
   */
  const currencies = [
    ...new Set(
      (plans.data?.data ?? []).flatMap((plan) => plan.prices.map((p) => p.currencyCode)),
    ),
  ].sort();

  /** What the server settled on, which is what the cards are showing. */
  const shownCurrency =
    currency ?? plans.data?.data.find((plan) => plan.billingCurrency)?.billingCurrency ?? null;

  if (subscription.isLoading || plans.isLoading) {
    return (
      <div className="grid place-items-center py-16">
        <Spinner label="Loading your plan" />
      </div>
    );
  }

  const sub = subscription.data;
  const openInvoice = invoices.data?.data.find((i) => i.status === 'open');

  /**
   * Staff can read the billing page and cannot act on it.
   *
   * Showing a Pay button to somebody whose role will refuse it is worse than
   * hiding it: they click, get a 403, and reasonably conclude the payment
   * system is broken rather than that it was never theirs to press.
   */
  const canPay = can('settings:update');

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Plan &amp; billing</h1>
        <p className="text-sm text-slate-500">
          What you are on, what it costs, and everything you have been charged.
        </p>
      </header>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {reference && confirmation.data && (
        <ConfirmationBanner
          status={confirmation.data.status}
          message={confirmation.data.message}
          onDismiss={() => {
            params.delete('reference');
            setParams(params, { replace: true });
            refreshAll();
          }}
        />
      )}

      {sub && (
        <StatusCard
          sub={sub}
          openInvoice={openInvoice}
          manualInstructions={plans.data?.paymentMode === 'manual' ? (plans.data.manualInstructions ?? null) : null}
        />
      )}

      {howToPay && (
        <div role="status" className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
          <p className="font-medium">How to pay</p>
          <p className="mt-0.5">{howToPay}</p>
          <button type="button" className="mt-1 text-xs underline" onClick={() => setHowToPay(null)}>
            Dismiss
          </button>
        </div>
      )}

      <Card>
        <CardHeader
          title="Choose a plan"
          subtitle="You are only charged when the payment goes through. Nothing changes before then."
          action={
            <div className="flex items-center gap-2">
              {currencies.length > 1 && (
                <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
                  {currencies.map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setCurrency(code)}
                      className={`rounded-md px-3 py-1 text-sm ${
                        code === shownCurrency
                          ? 'bg-slate-900 text-white'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      {code}
                    </button>
                  ))}
                </div>
              )}

              <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
                {(['monthly', 'yearly'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setInterval(option)}
                    className={`rounded-md px-3 py-1 text-sm capitalize ${
                      interval === option
                        ? 'bg-indigo-700 text-white'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          }
        />

        <div className="grid gap-4 p-4 pt-0 md:grid-cols-2 xl:grid-cols-4">
          {plans.data?.data.map((plan) => (
            <PlanCard
              key={plan.key}
              plan={plan}
              context={{
                interval,
                pendingKey: sub?.pendingPlanKey ?? null,
                pendingInterval: sub?.pendingInterval ?? null,
                paymentMode: plans.data?.paymentMode ?? 'online',
                canPay,
              }}
              busy={checkout.isPending}
              onPay={(needsConfirm, warnings) =>
                needsConfirm ? setConfirming({ plan, warnings }) : checkout.mutate({ planKey: plan.key })
              }
              onShowInstructions={() =>
                setHowToPay(
                  `${openInvoice ? `Invoice ${openInvoice.number} — ${money(openInvoice.amount, openInvoice.currencyCode)}. ` : ''}` +
                    (plans.data?.manualInstructions ?? ''),
                )
              }
            />
          ))}
        </div>
      </Card>

      <Card>
        <CardHeader title="Billing history" subtitle="Every invoice, paid or not." />
        {invoices.isLoading ? (
          <div className="p-4">
            <Spinner />
          </div>
        ) : !invoices.data?.data.length ? (
          <p className="p-4 text-sm text-slate-500">Nothing billed yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2">Invoice</th>
                  <th className="px-4 py-2">Plan</th>
                  <th className="px-4 py-2">Billed</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2">Status</th>
                  {canPay && <th className="px-4 py-2" />}
                </tr>
              </thead>
              <tbody>
                {invoices.data.data.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-2 font-mono text-xs text-slate-500">{invoice.number}</td>
                    <td className="px-4 py-2">{invoice.planName}</td>
                    <td className="px-4 py-2 capitalize text-slate-600">{invoice.interval}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {money(invoice.amount, invoice.currencyCode)}
                    </td>
                    <td className="px-4 py-2">
                      <Badge
                        tone={
                          invoice.status === 'paid'
                            ? 'success'
                            : invoice.status === 'open'
                              ? 'warning'
                              : 'neutral'
                        }
                      >
                        {invoice.status === 'open' ? 'Awaiting payment' : invoice.status}
                      </Badge>
                    </td>
                    {canPay && (
                      <td className="px-4 py-2 text-right">
                        {invoice.status === 'open' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={payInvoice.isPending}
                            onClick={() => payInvoice.mutate(invoice.id)}
                          >
                            Pay
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {sub && sub.status !== 'trialing' && canPay && (
        <Card>
          <CardHeader title="Cancel" subtitle="Your records stay yours, and stay exportable." />
          <div className="p-4 pt-0">
            {sub.cancelAtPeriodEnd ? (
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-amber-800">
                  Cancelling on {new Date(sub.periodEnd).toLocaleDateString()}. Until then nothing
                  changes.
                </p>
                <Button variant="secondary" onClick={() => resume.mutate()}>
                  Keep my subscription
                </Button>
              </div>
            ) : (
              <Button variant="secondary" onClick={() => setCancelling(true)}>
                Cancel subscription
              </Button>
            )}
          </div>
        </Card>
      )}

      <CapacityCard />

      <AiUsageCard />

      <Modal
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title={confirming ? `Move to ${confirming.plan.name}?` : ''}
      >
        {confirming && (
          <div className="space-y-4 text-sm">
            <p className="text-slate-600">
              {confirming.plan.name} would change what this business can do. Nothing changes until
              the invoice is paid, and nothing is deleted either way.
            </p>
            <ul className="list-disc space-y-1 pl-5 text-amber-900">
              {confirming.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirming(null)}>
                Keep looking
              </Button>
              <Button
                disabled={checkout.isPending}
                onClick={() => checkout.mutate({ planKey: confirming.plan.key, acknowledgeChanges: true })}
              >
                Choose {confirming.plan.name} anyway
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={cancelling} onClose={() => setCancelling(false)} title="Cancel subscription">
        <CancelForm
          endsAt={sub?.periodEnd ?? ''}
          pending={cancel.isPending}
          onCancel={() => setCancelling(false)}
          onConfirm={(reason) => cancel.mutate(reason)}
        />
      </Modal>
    </div>
  );
}

function ConfirmationBanner({
  status,
  message,
  onDismiss,
}: {
  status: string;
  message: string;
  onDismiss: () => void;
}): ReactNode {
  const good = status === 'activated' || status === 'already_active';
  const pending = status === 'pending';

  return (
    <div
      className={`flex items-start justify-between gap-4 rounded-lg border px-4 py-3 text-sm ${
        good
          ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
          : pending
            ? 'border-amber-200 bg-amber-50 text-amber-900'
            : 'border-red-200 bg-red-50 text-red-900'
      }`}
    >
      <div className="flex items-start gap-2">
        {good ? (
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
        ) : pending ? (
          <Clock className="mt-0.5 h-4 w-4 shrink-0" />
        ) : (
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        )}
        <span>{message}</span>
      </div>
      <button type="button" onClick={onDismiss} className="text-xs underline">
        Dismiss
      </button>
    </div>
  );
}

function StatusCard({
  sub,
  openInvoice,
  manualInstructions,
}: {
  sub: Subscription;
  openInvoice: Invoice | undefined;
  manualInstructions: string | null;
}): ReactNode {
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4 p-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-slate-900">{sub.planName}</h2>
            <Badge tone={subscriptionStatusBadge(sub).tone}>
              {subscriptionStatusBadge(sub).label}
            </Badge>
          </div>

          {sub.inTrial && (
            <p className="mt-1 text-sm text-slate-600">
              {sub.trialDaysLeft} day{sub.trialDaysLeft === 1 ? '' : 's'} left in your trial. Choose
              a plan before it ends to keep recording.
            </p>
          )}
          {sub.trialExpired && (
            <p className="mt-1 text-sm text-red-700">
              Your trial has ended. Your records are safe and still exportable — choose a plan to
              carry on recording.
            </p>
          )}
          {sub.status === 'past_due' && sub.graceUntil && (
            <p className="mt-1 text-sm text-amber-800">
              We could not take payment. Everything keeps working until{' '}
              {new Date(sub.graceUntil).toLocaleDateString()}.
            </p>
          )}
          {sub.complimentaryUntil && (
            <p className="mt-1 text-sm text-slate-600">
              Authorized by CenoX for internal use · no charge · review by{' '}
              {new Date(sub.complimentaryUntil).toLocaleDateString()}
            </p>
          )}
          {sub.status === 'active' && !sub.cancelAtPeriodEnd && !sub.complimentaryUntil && (
            <p className="mt-1 text-sm text-slate-500">
              Renews {new Date(sub.periodEnd).toLocaleDateString()} · billed {sub.interval}
            </p>
          )}
        </div>

        {/*
          A pending plan change is shown as pending, never as done. This is the
          single place a customer is most likely to misread the account — they
          clicked a plan, went to Paystack, and came back without paying.
        */}
        {openInvoice && sub.pendingPlanKey && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <p className="font-medium">
              {openInvoice.planName} is awaiting payment of{' '}
              {money(openInvoice.amount, openInvoice.currencyCode)}
            </p>
            <p className="mt-0.5 text-xs">
              Your plan has not changed yet. It changes when the payment clears.
            </p>
            {manualInstructions && (
              <p className="mt-1 text-xs">
                <strong>To pay invoice {openInvoice.number}:</strong> {manualInstructions}
              </p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function PlanCard({
  plan,
  context,
  busy,
  onPay,
  onShowInstructions,
}: {
  plan: Plan;
  context: Parameters<typeof planCardView>[1];
  busy: boolean;
  onPay: (needsConfirm: boolean, warnings: string[]) => void;
  onShowInstructions: () => void;
}): ReactNode {
  const view = planCardView(plan, context);
  const highlighted = view.badge !== null;

  return (
    <div
      className={`flex flex-col rounded-xl border p-4 ${
        highlighted ? 'border-indigo-300 bg-indigo-50/40' : 'border-slate-200'
      }`}
    >
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">{plan.name}</h3>
        {view.badge && <Badge tone={view.badge.tone}>{view.badge.label}</Badge>}
      </div>

      <p className="mt-1 min-h-10 text-xs text-slate-500">{plan.description}</p>

      <p className="mt-3 text-2xl font-semibold tabular-nums text-slate-900">
        {view.price ? (
          <>
            {money(view.price.amount, view.price.currencyCode)}
            <span className="text-sm font-normal text-slate-500">
              /{context.interval === 'yearly' ? 'yr' : 'mo'}
            </span>
          </>
        ) : (
          <span className="text-base font-medium">Talk to us</span>
        )}
      </p>
      {view.limitsLine && <p className="mt-1 text-xs text-slate-500">{view.limitsLine}</p>}

      <ul className="mt-3 flex-1 space-y-1 text-xs text-slate-600">
        {view.modules.map((m) => (
          <li key={m.key}>
            • {m.label}
            {m.note && <span className="text-slate-400"> — {m.note}</span>}
          </li>
        ))}
      </ul>

      {view.warnings.length > 0 && (
        <ul className="mt-3 space-y-1 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          {view.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}

      <div className="mt-4">
        {view.action.kind === 'pay' ? (
          <Button
            className="w-full"
            disabled={busy}
            onClick={() =>
              onPay(view.action.kind === 'pay' && view.action.needsConfirm, view.warnings)
            }
          >
            <CreditCard className="mr-1.5 h-4 w-4" />
            {busy ? 'Starting…' : view.action.label}
          </Button>
        ) : view.action.kind === 'instructions' ? (
          <Button variant="secondary" className="w-full" onClick={onShowInstructions}>
            {view.action.label}
          </Button>
        ) : (
          <Button variant="secondary" disabled className="w-full">
            {view.action.label}
          </Button>
        )}
      </div>
    </div>
  );
}

function CancelForm({
  endsAt,
  pending,
  onCancel,
  onConfirm,
}: {
  endsAt: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}): ReactNode {
  const [reason, setReason] = useState('');
  const until = endsAt ? new Date(endsAt).toLocaleDateString() : 'the end of the period';

  return (
    <div className="space-y-4">
      <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
        Your subscription runs until {until} — you have paid for it, so nothing stops before then.
        After that you will need a plan to carry on recording. Your records stay yours either way.
      </p>
      <input
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        placeholder="Why are you leaving? (helps us)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Keep it
        </Button>
        <Button
          variant="danger"
          disabled={reason.trim().length < 3 || pending}
          onClick={() => onConfirm(reason.trim())}
        >
          {pending ? 'Cancelling…' : 'Cancel subscription'}
        </Button>
      </div>
    </div>
  );
}
